import {
  buildPublicProfileRenderBlocks,
  getPublishedPublicProfileBlocks,
  type PublicProfileEffectivePrivacy,
  type PublicProfileContact,
  type PublicProfileMode,
  type PublicProfilePublicCompanyPage,
  type PublicProfilePublicPage,
  type PublicProfilePublicUserPage,
  type PublicProfileRenderOpenRole,
  type PublicProfileSocial,
  type PublicProfileTeamMember,
  type PublicProfileUrls,
} from '@tabbio/types';
import { DEFAULT_PUBLIC_PROFILE_PRIVACY } from '@tabbio/utils/profile-privacy';
import type {
  PublicProfilePreviewDesignInput,
  PublicProfileSurfaceDocument,
} from '@tabbio/public-renderers';
import type {
  ProfileFeaturedProject,
  ProfileServiceOffer,
} from '../../lib/profile-api';
import type {
  PublicProfileBlock,
  PublicProfileContactChannelId,
  PublicProfileSocialId,
} from '../../lib/public-profile-builder';
import {
  PUBLIC_PROFILE_CONTACT_ROW_ORDER,
  PUBLIC_PROFILE_SOCIAL_ORDER_COMPANY,
  PUBLIC_PROFILE_TOP_SOCIALS,
} from '../../lib/public-profile-builder';
import { buildPublicMessageUrl } from '../../lib/public-web';
import {
  buildAppJobUrl,
  buildCanonicalJobUrl,
  buildPublicProfileUrls,
} from '@tabbio/utils';

type DraftProfile = {
  name: string;
  bio: string;
  location: string;
  website: string;
  /** Company mode only — persisted as `Company.tagline` / `owner.headline`. */
  tagline?: string;
};

/**
 * Hero copy, resolved exactly the way the published page resolves it.
 *
 * `packages/public-renderers/src/profile.tsx` and the apps/web `ProfileSurface`
 * treat `owner.headline` (the API's mapping of `Company.tagline`) and
 * `owner.bio` (`Company.description`) as two INDEPENDENT slots:
 *
 *   tagline     = trimToNull(owner.headline)
 *   description = trimToNull(owner.bio)
 *   primary     = tagline ?? description
 *   secondary   = tagline && description && description !== tagline
 *                   ? description : null
 *
 * The preview's only job is therefore to hand over what is actually STORED and
 * let the renderer decide. Defaulting an empty description to the "We're
 * hiring! Join {name}." placeholder pushed an invented sentence into the
 * description slot, so a company with a tagline and no description previewed a
 * second line its live page never shows. That placeholder is editor chrome: it
 * stays on `displayHeadline`, which feeds only the model's own `headline`
 * field and the SmartCV block summary — never the rendered hero slots.
 *
 * Company mode drops the description slot entirely: `Company.description` has
 * no editor left, so anything the preview carried over from it would be copy
 * the employer can see but never change.
 */
function resolvePreviewCopy(
  draftProfile: DraftProfile,
  isCompanyMode: boolean,
  defaultHeadline: string,
  companyDefaultHeadline: string,
) {
  const bio = draftProfile.bio.trim();
  const tagline = (draftProfile.tagline ?? '').trim();

  if (!isCompanyMode) {
    // Individual pages edit a single stored field, which fills the primary
    // slot; the placeholder keeps an empty personal preview legible.
    const headline = bio || defaultHeadline;
    return {
      headline,
      bio: headline,
      tagline: null as string | null,
      displayHeadline: headline,
    };
  }

  // Deliberately drops `bio`: it would resurface downstream as `owner.bio`, the
  // page `description`, and the career block summary, none of which a company
  // owner can edit. The stored column is left untouched.
  return {
    headline: tagline,
    bio: '',
    tagline: tagline || null,
    displayHeadline: tagline || companyDefaultHeadline,
  };
}

export type PublicProfilePreviewCompanyRole = {
  id: string;
  title: string;
  department?: string | null;
  location?: string | null;
  type?: string | null;
  remote?: 'remote' | 'hybrid' | 'on-site' | string | null;
  applications?: number | null;
  publicSlug?: string | null;
  canonicalUrl?: string | null;
  applyUrl?: string | null;
};

export type PublicProfilePreviewModel = {
  document: PublicProfileSurfaceDocument;
  design: PublicProfilePreviewDesignInput;
  displayName: string;
  headline: string;
  publicUrlLabel: string;
  isCompanyMode: boolean;
  hideBranding: boolean;
};

export type PublicProfilePreviewModelInput = {
  routeMode: PublicProfileMode;
  draftProfile: DraftProfile;
  username: string;
  avatarUrl: string;
  privacy?: PublicProfileEffectivePrivacy;
  publicBaseUrl: string;
  publicUrlLabel: string;
  blocks: PublicProfileBlock[];
  socials: PublicProfileSocial[];
  contacts: PublicProfileContact[];
  projects: ProfileFeaturedProject[];
  serviceOffers: ProfileServiceOffer[];
  teamMembers: PublicProfileTeamMember[];
  companyRoles: PublicProfilePreviewCompanyRole[];
  defaultHeadline: string;
  companyDefaultHeadline: string;
  design: PublicProfilePreviewDesignInput;
  remoteLabels: {
    remote: string;
    hybrid: string;
    onSite: string;
  };
  hideBranding: boolean;
};

function filterSocialsByPrivacy(
  socials: PublicProfileSocial[],
  privacy: PublicProfileEffectivePrivacy,
) {
  if (!privacy.showLinks) return [];
  return socials.filter((social) => {
    if (social.id === 'email') return privacy.showEmail;
    if (social.id === 'whatsapp') return privacy.showPhone;
    return true;
  });
}

function filterContactsByPrivacy(
  contacts: PublicProfileContact[],
  privacy: PublicProfileEffectivePrivacy,
) {
  return contacts.filter((contact) => {
    if (contact.id === 'email') return privacy.showEmail;
    if (
      contact.id === 'phone' ||
      contact.id === 'landline' ||
      contact.id === 'whatsapp'
    ) {
      return privacy.showPhone;
    }
    return true;
  });
}

function getOrderedTopSocials(
  socials: PublicProfileSocial[],
  mode: PublicProfileMode = 'individual',
) {
  // Company previews surface the Phase 1 platform set (PRD §6.2); individual
  // previews keep the historical top-five row.
  const topSocialIds = new Set<PublicProfileSocialId>(
    mode === 'company' ? PUBLIC_PROFILE_SOCIAL_ORDER_COMPANY : PUBLIC_PROFILE_TOP_SOCIALS,
  );
  return socials
    .filter((social) => social.enabled && social.url.trim() && topSocialIds.has(social.id))
    .sort((left, right) => left.order - right.order);
}

function getOrderedTopContacts(contacts: PublicProfileContact[], username: string) {
  const topContactIds = new Set<PublicProfileContactChannelId>(PUBLIC_PROFILE_CONTACT_ROW_ORDER);
  return contacts
    .filter(
      (contact) =>
        contact.enabled &&
        topContactIds.has(contact.id) &&
        (contact.id === 'tabbioMessage' || contact.value.trim()),
    )
    .sort((left, right) => left.order - right.order)
    .map((contact) =>
      contact.id === 'tabbioMessage'
        ? { ...contact, value: contact.value.trim() || buildPublicMessageUrl(username) || '' }
        : contact,
    );
}

function mapCompanyRole(
  role: PublicProfilePreviewCompanyRole,
  remoteLabels: PublicProfilePreviewModelInput['remoteLabels'],
): PublicProfileRenderOpenRole {
  const workMode =
    role.remote === 'remote'
      ? remoteLabels.remote
      : role.remote === 'hybrid'
        ? remoteLabels.hybrid
        : remoteLabels.onSite;

  return {
    id: role.id,
    title: role.title,
    description: [role.department, role.type].filter(Boolean).join(' • '),
    location: role.location,
    department: role.department,
    type: role.type,
    workMode,
    tags: [role.department, role.type, role.remote].filter(Boolean) as string[],
    applicationCount: role.applications,
    publicSlug: role.publicSlug ?? undefined,
    canonicalUrl:
      role.canonicalUrl ||
      (role.publicSlug ? buildCanonicalJobUrl(role.publicSlug) : buildAppJobUrl(role.id)),
    applyUrl:
      role.applyUrl ||
      role.canonicalUrl ||
      (role.publicSlug ? buildCanonicalJobUrl(role.publicSlug) : buildAppJobUrl(role.id)),
  };
}

function getPublicSiteUrlFromHubUrl(hubUrl: string, handle: string) {
  try {
    const parsed = new URL(hubUrl);
    const normalizedHandle = handle.trim().replace(/^@+/, '').toLowerCase();
    if (parsed.pathname.replace(/^\/+|\/+$/g, '') === normalizedHandle) {
      parsed.pathname = '/';
      parsed.search = '';
      parsed.hash = '';
      return parsed.toString();
    }
    return parsed.origin;
  } catch {
    return 'https://tabbio.com';
  }
}

function buildPreviewUrlSet(
  routeMode: PublicProfileMode,
  username: string,
  publicBaseUrl: string,
): PublicProfileUrls {
  const publicSiteUrl = getPublicSiteUrlFromHubUrl(publicBaseUrl, username);
  return routeMode === 'company'
    ? buildPublicProfileUrls('company', username, { publicSiteUrl })
    : buildPublicProfileUrls('user', username, { publicSiteUrl });
}

export function normalizeExternalUrl(value: string) {
  const clean = value.trim();
  if (!clean) return null;
  if (clean.startsWith('http://') || clean.startsWith('https://')) return clean;
  if (clean.includes('@') && !clean.includes(' ')) return `mailto:${clean}`;
  return `https://${clean}`;
}

export function normalizePhoneNumber(value: string) {
  const clean = value.trim();
  if (!clean) return '';
  return clean.replace(/[^\d+]/g, '');
}

export function getSocialActionUrl(id: PublicProfileSocialId, value: string) {
  if (id === 'email') {
    const clean = value.trim();
    return clean ? `mailto:${clean}` : null;
  }
  if (id === 'whatsapp') {
    const phone = normalizePhoneNumber(value);
    return phone ? `https://wa.me/${phone.replace('+', '')}` : null;
  }
  return normalizeExternalUrl(value);
}

export function getContactActionUrl(
  id: PublicProfileContactChannelId,
  value: string,
) {
  const clean = value.trim();
  if (!clean) return null;
  if (id === 'tabbioMessage') return normalizeExternalUrl(clean);
  if (id === 'email') return `mailto:${clean}`;
  if (id === 'phone' || id === 'landline') return `tel:${normalizePhoneNumber(clean)}`;
  if (id === 'whatsapp') {
    const phone = normalizePhoneNumber(clean);
    return phone ? `https://wa.me/${phone.replace('+', '')}` : null;
  }
  if (id === 'telegram') {
    if (clean.startsWith('http://') || clean.startsWith('https://')) return clean;
    const handle = clean.replace(/^@/, '');
    return handle ? `https://t.me/${handle}` : null;
  }
  return normalizeExternalUrl(clean);
}

export function createPublicProfilePreviewModel({
  routeMode,
  draftProfile,
  username,
  avatarUrl,
  privacy = DEFAULT_PUBLIC_PROFILE_PRIVACY,
  publicBaseUrl,
  publicUrlLabel,
  blocks,
  socials,
  contacts,
  projects,
  serviceOffers,
  teamMembers,
  companyRoles,
  defaultHeadline,
  companyDefaultHeadline,
  design,
  remoteLabels,
  hideBranding,
}: PublicProfilePreviewModelInput): PublicProfilePreviewModel {
  const isCompanyMode = routeMode === 'company';
  const effectivePrivacy = isCompanyMode ? DEFAULT_PUBLIC_PROFILE_PRIVACY : privacy;
  const fallbackName = username || 'Tabbio';
  const displayName = draftProfile.name.trim() || fallbackName;
  const copy = resolvePreviewCopy(
    draftProfile,
    isCompanyMode,
    defaultHeadline,
    companyDefaultHeadline,
  );
  const headline = copy.displayHeadline;
  const publicLocation = effectivePrivacy.showLocation
    ? draftProfile.location.trim()
    : '';
  const publicWebsite = effectivePrivacy.showLinks
    ? draftProfile.website.trim()
    : '';
  const publicAvatarUrl = effectivePrivacy.showPhoto ? avatarUrl : '';
  const publicSocials = filterSocialsByPrivacy(socials, effectivePrivacy);
  const publicContacts = filterContactsByPrivacy(contacts, effectivePrivacy);
  const urls = buildPreviewUrlSet(routeMode, username, publicBaseUrl);
  const renderBlocks = buildPublicProfileRenderBlocks({
    mode: routeMode,
    publicUrl: publicBaseUrl,
    urls,
    owner: {
      name: displayName,
      headline: copy.headline,
      bio: copy.bio,
      location: publicLocation || null,
      website: publicWebsite || null,
    },
    blocks,
    summary: headline,
    skills: [],
    highlights: [],
    experiences: [],
    projects,
    services: serviceOffers,
    teamMembers,
    openRoles: isCompanyMode
      ? companyRoles.map((role) => mapCompanyRole(role, remoteLabels))
      : [],
  });

  return {
    document: {
      ownerKind: isCompanyMode ? 'company' : 'user',
      owner: {
        kind: isCompanyMode ? 'company' : 'user',
        name: displayName,
        username,
        slug: isCompanyMode ? username : null,
        headline: copy.headline,
        bio: copy.bio,
        location: publicLocation || null,
        avatarUrl: publicAvatarUrl || null,
        website: publicWebsite || null,
      },
      canonicalHandle: username,
      publicUrl: publicBaseUrl,
      socials: getOrderedTopSocials(publicSocials, routeMode),
      contacts: getOrderedTopContacts(publicContacts, username),
      blocks: getPublishedPublicProfileBlocks(renderBlocks, { isCompany: isCompanyMode }),
    },
    design,
    displayName,
    headline,
    publicUrlLabel,
    isCompanyMode,
    hideBranding,
  };
}

/**
 * Adapter that emits a `PublicProfilePublicPage` shape from the same editor
 * draft inputs. Consumed by the live-preview iframe bridge: the apps/web
 * `ProfileSurface` reads only the fields a published page exposes, so we fill
 * the deeper SmartCV / settings stubs with safe defaults.
 */
export function buildPublicProfilePreviewPage({
  routeMode,
  draftProfile,
  username,
  avatarUrl,
  privacy = DEFAULT_PUBLIC_PROFILE_PRIVACY,
  publicBaseUrl,
  blocks,
  socials,
  contacts,
  projects,
  serviceOffers,
  teamMembers,
  companyRoles,
  defaultHeadline,
  companyDefaultHeadline,
  design,
  hideBranding,
  remoteLabels,
}: PublicProfilePreviewModelInput): PublicProfilePublicPage {
  const isCompanyMode = routeMode === 'company';
  const effectivePrivacy = isCompanyMode ? DEFAULT_PUBLIC_PROFILE_PRIVACY : privacy;
  const fallbackName = username || 'Tabbio';
  const displayName = draftProfile.name.trim() || fallbackName;
  const copy = resolvePreviewCopy(
    draftProfile,
    isCompanyMode,
    defaultHeadline,
    companyDefaultHeadline,
  );
  const headline = copy.displayHeadline;
  const publicLocation = effectivePrivacy.showLocation
    ? draftProfile.location.trim()
    : '';
  const publicWebsite = effectivePrivacy.showLinks
    ? draftProfile.website.trim()
    : '';
  const publicAvatarUrl = effectivePrivacy.showPhoto ? avatarUrl : '';
  const publicSocials = filterSocialsByPrivacy(socials, effectivePrivacy);
  const publicContacts = filterContactsByPrivacy(contacts, effectivePrivacy);
  const publicSiteUrl = getPublicSiteUrlFromHubUrl(publicBaseUrl, username);

  const owner = {
    kind: isCompanyMode ? ('company' as const) : ('user' as const),
    name: displayName,
    username,
    slug: isCompanyMode ? username : null,
    headline: copy.headline,
    bio: copy.bio,
    location: publicLocation || null,
    avatarUrl: publicAvatarUrl || null,
    website: publicWebsite || null,
    coverImageUrl: null,
  };

  const base = {
    pageId: `preview:${username || 'tabbio'}`,
    publicUrl: publicBaseUrl,
    canonicalHandle: username,
    prettyUrl: null,
    searchEngineIndexingAllowed: false,
    tracking: { viewPixelUrl: '', redirectBaseUrl: '' },
    owner,
    // `PublicProfilePreviewDesignInput` (from @tabbio/public-renderers) and
    // `PublicProfileDesign` overlap structurally; the renderer never reads
    // `sharePreviewBackground` so this cast is safe at runtime.
    design: { sharePreviewBackground: 'gradient', ...design } as unknown as PublicProfilePublicCompanyPage['design'],
    settings: {
      hideBranding,
      sensitiveContent: false,
      showContactChannelsPublicly: true,
    },
    blocks,
    privacy: effectivePrivacy,
    socials: publicSocials,
    contacts: publicContacts,
  };

  if (isCompanyMode) {
    const companyPage: PublicProfilePublicCompanyPage = {
      ...base,
      ownerKind: 'company',
      urls: buildPublicProfileUrls('company', username, { publicSiteUrl }),
      tagline: copy.tagline,
      // The live company page no longer renders a description and there is no
      // editor for it, so the preview shows none either.
      description: null,
      industry: null,
      employeeCount: null,
      teamMembers,
      // ProfileSurface only reads the listed PublicProfileRenderOpenRole fields
      // from these preview-only fixtures, so this cast is safe.
      openRoles: companyRoles.map((role) => {
        const workMode =
          role.remote === 'remote'
            ? remoteLabels.remote
            : role.remote === 'hybrid'
              ? remoteLabels.hybrid
              : remoteLabels.onSite;
        return {
          id: role.id,
          title: role.title,
          department: role.department ?? null,
          location: role.location ?? null,
          type: role.type ?? 'Full-time',
          workMode,
          level: '',
          publicSlug: role.publicSlug ?? undefined,
          canonicalUrl: role.canonicalUrl ?? undefined,
          applyUrl: role.applyUrl ?? null,
          applicationCount: role.applications ?? 0,
        };
      }) as unknown as PublicProfilePublicCompanyPage['openRoles'],
    };
    return companyPage;
  }

  const userPage: PublicProfilePublicUserPage = {
    ...base,
    ownerKind: 'user',
    urls: buildPublicProfileUrls('user', username, { publicSiteUrl }),
    summary: headline,
    skills: [],
    skillGroups: [],
    languages: [],
    // CVConfigData is opaque to the surface; an empty object is sufficient.
    cvConfig: {} as PublicProfilePublicUserPage['cvConfig'],
    highlights: [],
    experiences: [],
    educations: [],
    certifications: [],
    projects: projects.map((project) => ({
      id: project.id,
      title: project.title,
      description: project.description ?? null,
      image: project.image ?? null,
      link: project.link ?? null,
      technologies: project.technologies ?? [],
    })),
    services: serviceOffers.map((service) => ({
      id: service.id,
      title: service.title,
      description: service.description ?? null,
      basePrice:
        typeof service.basePrice === 'number' && Number.isFinite(service.basePrice)
          ? String(service.basePrice)
          : null,
      deliveryDays:
        typeof service.deliveryDays === 'number' && Number.isFinite(service.deliveryDays)
          ? service.deliveryDays
          : null,
      deliverables: service.deliverables ?? [],
    })),
  };
  return userPage;
}
